import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, CheckCircle2, FileSpreadsheet, GitBranch, Lock, Cpu, Globe } from 'lucide-react';

export default function BancaAigisCaseStudy() {
  // Animation settings
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    whileInView: { transition: { staggerChildren: 0.2 } }
  };

  return (
    <section className="py-5 bg-white" style={{ overflow: 'hidden' }}>
      <div className="container">
        
        {/* --- SECTION HEADER --- */}
        <motion.div 
          className="text-center mb-5"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <h6 className="text-darkpurple fw-bold text-uppercase mb-2" style={{ letterSpacing: '2px' }}>
            Success Story
          </h6>
          <h2 className="display-4 fw-bold mb-3">Digital Banking & Compliance Transformation</h2>
          <div className="mx-auto bg-darkpurple mb-4" style={{ height: '4px', width: '60px', borderRadius: '2px' }}></div>
          <p className="lead text-secondary mx-auto" style={{ maxWidth: '800px' }}>
            LuranTech developed an end-to-end <strong>Digital Finance Ecosystem</strong> for a European Digital Bank, 
            integrating complex regulatory workflows with a seamless user experience.
          </p>
        </motion.div>

        <div className="row g-4 align-items-stretch">
          
          {/* --- CHALLENGE CARD --- */}
          <motion.div className="col-lg-6" {...fadeInUp}>
            <div className="card h-100 border-0 shadow-lg p-4 p-md-5" style={{ borderRadius: '20px', background: '#f8f9fa' }}>
              <div className="d-flex align-items-center mb-4">
                <div className="bg-white shadow-sm p-3 rounded-circle me-3">
                  <Lock className="text-darkpurple" size={28} />
                </div>
                <h3 className="h4 fw-bold mb-0">The Challenge</h3>
              </div>
              <p className="text-muted mb-4">
                The bank required a secure, multilingual platform capable of handling high-stakes finance requests while maintaining strict regulatory compliance.
              </p>
              <ul className="list-unstyled">
                {[
                  'Bridging the gap between customer requests and internal audit trails',
                  'Ensuring secure, remote identity verification (KYC/Video)',
                  'Managing complex, multi-level approval workflows across departments',
                  'Consolidating diverse financial data into actionable reports'
                ].map((item, i) => (
                  <li key={i} className="mb-3 d-flex align-items-start">
                    <span className="badge rounded-circle bg-danger bg-opacity-10 text-danger me-2 mt-1 px-2 pb-1">!</span>
                    <span className="text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* --- SOLUTION CARD --- */}
          <motion.div className="col-lg-6" {...fadeInUp} transition={{ delay: 0.2 }}>
            <div className="card h-100 border-0 shadow-lg p-4 p-md-5 text-white" 
                 style={{ borderRadius: '20px', background: 'linear-gradient(279.39deg, #5060E5 -11.76%, #7F60A6 35.07%, #352482 80.72%)' }}>
              <div className="d-flex align-items-center mb-4">
                <div className="bg-white bg-opacity-20 p-3 rounded-circle me-3">
                  <Cpu className="text-darkpurple" size={28} />
                </div>
                <h3 className="h4 fw-bold mb-0">The LuranTech Solution</h3>
              </div>
              <p className="text-white text-opacity-75 mb-4">
                We engineered a robust portal using Liferay 7 and Java, optimized with Redis for high-performance financial processing.
              </p>
              <ul className="list-unstyled">
                {[
                  'Integrated KYC and Video Verification for secure remote onboarding',
                  'Automated Kaleo Workflow engine for streamlined finance approvals',
                  'Dynamic reporting module with deep-dive parameter filtering',
                  'Scalable multilingual CMS for global accessibility'
                ].map((item, i) => (
                  <li key={i} className="mb-3 d-flex align-items-start">
                    <CheckCircle2 className="me-2 text-white mt-1" size={20} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* --- RESULTS GRID --- */}
        <motion.div 
          className="row g-4 mt-5"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          {[
            { 
              icon: <ShieldCheck className="text-darkpurple" size={32} />, 
              title: 'Secure Onboarding', 
              desc: 'Seamless KYC & Video verification integration.' 
            },
            { 
              icon: <GitBranch className="text-darkpurple" size={32} />, 
              title: 'Smart Workflow', 
              desc: 'Automated Kaleo-driven request management.' 
            },
            { 
              icon: <Globe className="text-darkpurple" size={32} />, 
              title: 'Multilingual', 
              desc: 'Localized banking experience for all users.' 
            },
            { 
              icon: <FileSpreadsheet className="text-darkpurple" size={32} />, 
              title: 'Advanced Reporting', 
              desc: 'Granular financial insights and data exports.' 
            }
          ].map((stat, index) => (
            <motion.div key={index} className="col-6 col-md-3" variants={fadeInUp}>
              <div className="text-center p-4 border-0 h-100 transition-card">
                <div className="text-primary mb-3 d-flex justify-content-center">{stat.icon}</div>
                <h5 className="fw-bold mb-2">{stat.title}</h5>
                <p className="text-muted small mb-0">{stat.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .transition-card {
          transition: all 0.3s ease;
          border-radius: 15px;
        }
        .transition-card:hover {
          background: #ffffff;
          box-shadow: 0 10px 30px rgba(0,0,0,0.08);
          transform: translateY(-5px);
        }
      `}} />
    </section>
  );
}